import React from "react";
import { useTranslation } from 'react-i18next';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Database, Globe, ExternalLink, Settings } from "lucide-react";
import { USAMapNavigator } from "@/components/USAMapNavigator";
import { withScenario } from "@/lib/mapNav";
import { gradeColor } from "@/lib/grades";
import type { Hospital, HospitalStats, DebugToggles, ScenarioInfo } from "@/types/map";

interface MapTopOverlaysProps {
  hospitalStats: HospitalStats;
  debugToggles: DebugToggles;
  isProceduresExpanded: boolean;

  // Scenario
  activeScenarioId: string | null;
  setActiveScenarioId: (v: string | null) => void;
  scenarioInfo: ScenarioInfo | null;
  setSearchParams: (v: any) => void;

  // Hospital search
  hospitalSearch: string;
  setHospitalSearch: (s: string) => void;
  showHospitalDropdown: boolean;
  setShowHospitalDropdown: (v: boolean) => void;
  filteredHospitals: Hospital[];

  // Actions
  setSelectedHospital: (h: Hospital | null) => void;
  fetchHospitalProcedures: (hospitalId: string) => void;
  orbitHospital: (hospital: Hospital) => void;
  flyToState: (stateCode: string) => void;
  navigate: (path: string, options?: any) => void;
  setSelectedTableState: (s: string) => void;
  setSelectedTableCity: (s: string) => void;

  // Marker settings
  maxMarkers: number;
  setMaxMarkers: (v: number) => void;
  markerSortOrder: 'cheapest' | 'expensive';
  setMarkerSortOrder: (v: 'cheapest' | 'expensive') => void;
  showMarkerSettings: boolean;
  setShowMarkerSettings: (v: boolean) => void;
  hospitals: Hospital[];
  viewerRef: React.MutableRefObject<any>;
  addHospitalMarkers: (viewer: any) => void;
  toast: any;

  // Brand
  isAhp: boolean;

  /** OSM (Leaflet) map is driving — markers are A–F grade badges, so the
   *  legend must describe grades, not the old outlier-percentage colors. */
  osmActive?: boolean;
}

export function MapTopOverlays({
  hospitalStats,
  debugToggles,
  isProceduresExpanded,
  activeScenarioId,
  setActiveScenarioId,
  scenarioInfo,
  setSearchParams,
  osmActive = false,
  hospitalSearch,
  setHospitalSearch,
  showHospitalDropdown,
  setShowHospitalDropdown,
  filteredHospitals,
  setSelectedHospital,
  fetchHospitalProcedures,
  orbitHospital,
  flyToState,
  navigate,
  setSelectedTableState,
  setSelectedTableCity,
  maxMarkers,
  setMaxMarkers,
  markerSortOrder,
  setMarkerSortOrder,
  showMarkerSettings,
  setShowMarkerSettings,
  hospitals,
  viewerRef,
  addHospitalMarkers,
  toast,
  isAhp,
}: MapTopOverlaysProps) {
  const { t } = useTranslation('map');
  return (
    <>
      {/* Scenario context banner — names the scenario the map's grades and
          prices are for, so a marker's scenario grade is never mistaken for
          the hospital's overall grade shown in the right panel. */}
      {activeScenarioId && scenarioInfo && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[60] hidden lg:flex items-center gap-2.5 px-4 py-2 rounded-full shadow-lg"
             style={{ background: 'rgba(var(--civic-navy-rgb),0.94)', border: '1.5px solid rgba(var(--civic-gold-rgb),0.6)', maxWidth: '46vw' }}>
          <span style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '.08em', color: 'var(--civic-gold)', whiteSpace: 'nowrap' }}>
            MAP SHOWING
          </span>
          <span style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontSize: 17, color: '#f2ecdd', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {scenarioInfo.name}
          </span>
          <span style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: 11.5, color: '#9fb0c8', whiteSpace: 'nowrap' }}>
            · pin grades are for this scenario
          </span>
        </div>
      )}

      {/* Top overlay row: Legend + Search + Quick Nav in a flex row */}
      <div className={`absolute top-4 left-[420px] items-start gap-4 transition-all duration-300 hidden lg:flex ${
        isProceduresExpanded ? 'z-[50]' : 'z-10'
      }`} style={{ right: '610px', display: debugToggles.topOverlays ? '' : 'none' }}>
        {/* Hospital Statistics - hidden for consumer brand */}
        {!isAhp && (
          <div className="shrink-0 bg-background/95 backdrop-blur-sm rounded-lg p-4 shadow-lg border border-border min-w-[200px]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground">Database Statistics</h3>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => window.open('/ingestion-dashboard', '_blank')}
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                Dashboard
              </Button>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center gap-4">
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Database className="w-3 h-3" />
                  Hospitals:
                </span>
                <span className="text-xs font-medium text-green-500">
                  {hospitalStats.total?.toLocaleString() || '0'}
                </span>
              </div>
              <div className="flex justify-between items-center gap-4">
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  Followed:
                </span>
                <span className="text-xs font-medium text-purple-400">
                  {hospitalStats.followed?.toLocaleString() || '0'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Legend -- switches between outlier mode and scenario mode.
            OSM mode: markers are A–F grade badges, so describe the grade scale
            (same boundaries as /hospital-rankings) instead of outlier colors. */}
        <div className="shrink-0 bg-background/95 backdrop-blur-sm rounded-lg p-4 shadow-lg border border-border max-w-[237px]">
          {osmActive ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground">
                  {activeScenarioId ? t('legend.scenarioTitle', { defaultValue: 'This scenario · vs natl avg' }) : t('legend.rankingsTitle', { defaultValue: 'Grade · vs natl median' })}
                </h3>
                {activeScenarioId && scenarioInfo && (
                  <button
                    onClick={() => { setActiveScenarioId(null); setSearchParams({}); }}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    {t('scenario.exitScenario')}
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {/* Scenario grades use the avg-baseline scale; hospital grades
                    use the median-baseline scale (see grades.ts). Ranges read
                    as price-vs-baseline: markup -45% = paying 55% of the
                    national median. */}
                {(activeScenarioId ? [
                  ['A', t('legend.gradeA', { defaultValue: 'A · ≤30% of natl avg' })],
                  ['B', t('legend.gradeB', { defaultValue: 'B · 30–65%' })],
                  ['C', t('legend.gradeC', { defaultValue: 'C · 65–95%' })],
                  ['D', t('legend.gradeD', { defaultValue: 'D · 95–130%' })],
                  ['F', t('legend.gradeF', { defaultValue: 'F · >130% of natl avg' })],
                ] as const : [
                  ['A', t('legend.hospitalGradeA', { defaultValue: 'A · ≤55% of natl median' })],
                  ['B', t('legend.hospitalGradeB', { defaultValue: 'B · 55–120%' })],
                  ['C', t('legend.hospitalGradeC', { defaultValue: 'C · 120–190%' })],
                  ['D', t('legend.hospitalGradeD', { defaultValue: 'D · 190–275%' })],
                  ['F', t('legend.hospitalGradeF', { defaultValue: 'F · >275% of natl median' })],
                ] as const).map(([g, label]) => (
                  <div key={g} className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-white font-extrabold"
                      style={{ backgroundColor: gradeColor(g), fontSize: 9 }}
                    >{g}</div>
                    <span className="text-xs">{label}</span>
                  </div>
                ))}
                <div className="mt-2 pt-2 border-t border-border/30">
                  <p className="text-xs text-muted-foreground italic">
                    {t('legend.rankHint', { defaultValue: 'Zoom in for each hospital’s national rank' })}
                  </p>
                  {activeScenarioId && scenarioInfo && (
                    <p className="text-xs text-muted-foreground italic truncate mt-1" title={scenarioInfo.name}>
                      {scenarioInfo.name}
                    </p>
                  )}
                </div>
              </div>
            </>
          ) : activeScenarioId && scenarioInfo ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground">{t('legend.priceLevel')}</h3>
                <button
                  onClick={() => { setActiveScenarioId(null); setSearchParams({}); }}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  {t('scenario.exitScenario')}
                </button>
              </div>
              {/* Tiers mirror scenarioMarkerColor() in lib/grades.ts */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#16a34a' }}></div>
                  <span className="text-xs">{t('legend.belowAverage')} (&le;-5%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#eab308' }}></div>
                  <span className="text-xs">{t('legend.moderate')} (-5-30%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#ea580c' }}></div>
                  <span className="text-xs">{t('legend.high')} (30-150%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#dc2626' }}></div>
                  <span className="text-xs">{t('legend.veryHigh')} (&gt;150%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#6b7280' }}></div>
                  <span className="text-xs">{t('tooltip.coverage')}: N/A</span>
                </div>
                <div className="mt-2 pt-2 border-t border-border/30">
                  <p className="text-xs text-muted-foreground italic truncate" title={scenarioInfo.name}>
                    {scenarioInfo.name}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-sm font-semibold text-foreground mb-3">{t('legend.priceLevel')}</h3>
              {/* Tiers mirror getMarkerColor() in lib/markerColors.ts */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"></div>
                  <span className="text-xs">{t('legend.low')} (&lt;10%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500 flex-shrink-0"></div>
                  <span className="text-xs">{t('legend.moderate')} (10-24%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500 flex-shrink-0"></div>
                  <span className="text-xs">{t('legend.high')} (25-39%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0"></div>
                  <span className="text-xs">{t('legend.veryHigh')} (40%+)</span>
                </div>
                <div className="mt-3 pt-3 border-t border-border/30">
                  <p className="text-xs text-muted-foreground italic whitespace-nowrap">
                    Outlier = price &gt;50% above avg
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Hospital Search - fills remaining space between legend and quick nav */}
        <div className="flex-1 min-w-[150px] bg-background/95 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-border">
          <h3 className="text-xs font-semibold text-foreground mb-2">{t('filters.searchHospitals')}</h3>
          <div className="relative">
            <Input
              placeholder={t('filters.searchHospitals')}
              value={hospitalSearch}
              onChange={(e) => {
                setHospitalSearch(e.target.value);
                setShowHospitalDropdown(e.target.value.length > 0);
              }}
              onFocus={() => setShowHospitalDropdown(hospitalSearch.length > 0)}
              onBlur={() => setTimeout(() => setShowHospitalDropdown(false), 200)}
              className="w-full text-sm"
            />
            {showHospitalDropdown && filteredHospitals.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
                {filteredHospitals.map((hospital) => (
                  <button
                    key={hospital.id}
                    onClick={() => {
                      setSelectedHospital(hospital);
                      fetchHospitalProcedures(hospital.id);
                      orbitHospital(hospital);
                      navigate(withScenario(`/map/${hospital.state.toLowerCase()}/${hospital.id}`), { replace: true });
                      setHospitalSearch('');
                      setShowHospitalDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-accent border-b border-border/30 last:border-b-0"
                  >
                    <div className="font-medium text-sm text-foreground">{hospital.name}</div>
                    <div className="text-xs text-muted-foreground">{hospital.city}, {hospital.state}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Navigation + Marker Filters */}
        <div className="shrink-0 flex flex-col gap-2">
          <div className="bg-background/95 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-border">
            <h3 className="text-xs font-semibold text-foreground mb-2">{t('navigation.flyToState')}</h3>
            <USAMapNavigator
              onStateClick={(stateCode) => {
                flyToState(stateCode);
                setSelectedTableState(stateCode);
                setSelectedTableCity('');
                setSelectedHospital(null);
                navigate(withScenario(`/map/${stateCode.toLowerCase()}`), { replace: true });
              }}
            />
            <button
              onClick={() => {
                flyToState('USA');
                setSelectedTableState('USA');
                setSelectedTableCity('');
                setSelectedHospital(null);
                navigate(withScenario('/map'), { replace: true });
              }}
              className="w-full text-center px-2 py-1 mt-2 text-xs bg-primary/10 hover:bg-primary/20 rounded transition-colors"
            >
              View All USA
            </button>
          </div>

          {/* Marker Filter Settings - Below Quick Navigation */}
          <div className="bg-background/95 backdrop-blur-sm rounded-lg p-2 shadow-lg border border-border">
            <Dialog open={showMarkerSettings} onOpenChange={setShowMarkerSettings}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs"
                >
                  <Settings className="w-3 h-3 mr-2" />
                  {t('filters.markerSettings')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>{t('filters.markerSettings')}</DialogTitle>
                  <DialogDescription>
                    Customize how markers are displayed on the map
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  {/* Max Markers Slider */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <label className="text-sm font-medium">{t('filters.maxMarkers')}</label>
                      <span className="text-sm text-muted-foreground">{maxMarkers}</span>
                    </div>
                    <Slider
                      value={[maxMarkers]}
                      onValueChange={(value) => setMaxMarkers(value[0])}
                      min={250}
                      max={2000}
                      step={250}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Low (250)</span>
                      <span>Medium (1000)</span>
                      <span>High (2000)</span>
                    </div>
                  </div>

                  {/* Sort Order Toggle */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium">{t('filters.sortOrder')}</label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={markerSortOrder === 'expensive' ? 'default' : 'outline'}
                        onClick={() => setMarkerSortOrder('expensive')}
                        className={`${markerSortOrder === 'expensive' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                      >
                        {t('filters.expensive')}
                      </Button>
                      <Button
                        variant={markerSortOrder === 'cheapest' ? 'default' : 'outline'}
                        onClick={() => setMarkerSortOrder('cheapest')}
                        className={`${markerSortOrder === 'cheapest' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                      >
                        {t('filters.cheapest')}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {markerSortOrder === 'expensive'
                        ? 'Showing hospitals with the highest price markups'
                        : 'Showing hospitals with the lowest price markups'}
                    </p>
                  </div>

                  {/* Apply Button */}
                  <Button
                    onClick={() => {
                      setShowMarkerSettings(false);
                      toast({
                        title: "Settings Applied",
                        description: `Showing ${maxMarkers} ${markerSortOrder} hospitals`,
                      });
                      // Refresh markers
                      if (viewerRef.current && hospitals.length > 0) {
                        addHospitalMarkers(viewerRef.current);
                      }
                    }}
                    className="w-full"
                  >
                    Apply Changes
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Scenario Info Banner - floating top center (desktop only; mobile has its
          own compact chip rendered in HospitalMap so it can clear the FAB). */}
      {activeScenarioId && scenarioInfo && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 hidden lg:block">
          <div className={`${isAhp ? 'bg-[var(--civic-navy-deep)]/95 border-civic-gold/30' : 'bg-background/95 border-border'} backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg border flex items-center gap-3`}>
            <span className={`text-sm font-semibold ${isAhp ? 'text-white' : 'text-foreground'}`} style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif" } : undefined}>
              {scenarioInfo.name}
            </span>
            <span className="text-xs text-muted-foreground">|</span>
            <span className={`text-xs ${isAhp ? 'text-civic-gold' : 'text-matrix-green'}`}>
              {t('scenario.nationalAvg', { price: `$${scenarioInfo.nationalAvgPrice.toLocaleString()}` })}
            </span>
            <span className="text-xs text-muted-foreground">|</span>
            <span className="text-xs text-muted-foreground">
              {t('scenario.hospitalsWithData', { count: scenarioInfo.hospitalCount.toLocaleString() })}
            </span>
            <button
              onClick={() => { setActiveScenarioId(null); setSearchParams({}); }}
              className="ml-1 text-muted-foreground hover:text-foreground text-xs font-bold"
              title={t('scenario.exitScenario')}
            >
              &times;
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default MapTopOverlays;
